import type {
  ContractCostStats,
  ContractProjectCost,
  ContractUsage,
  CostRange,
  UsageProductType,
} from './usage-api';

/**
 * Calculs purs de l'écran « Usage et coûts » (lot L5), testés par `usage-model.test.ts`.
 *
 * Période : le backend retient par défaut les 365 derniers jours (`CostHandler` : `from` = now -
 * 365 j). La console propose 30, 90 et 365 jours ; 365 par défaut, comme le backend.
 */
export const USAGE_PERIODS = [30, 90, 365] as const;
export type UsagePeriod = (typeof USAGE_PERIODS)[number];
export const DEFAULT_PERIOD: UsagePeriod = 365;

export const asPeriod = (value: unknown): UsagePeriod =>
  USAGE_PERIODS.find((period) => String(period) === String(value)) ?? DEFAULT_PERIOD;

const DAY = 86_400_000;

/** Fenêtre `[now - jours, now]` au jour près (minuit UTC) : la clé de cache ne change pas à chaque rendu. */
export function rangeOf(days: UsagePeriod, now: Date): CostRange {
  const end = new Date(now);
  end.setUTCHours(23, 59, 59, 0);
  const start = new Date(end.getTime() - days * DAY);
  start.setUTCHours(0, 0, 0, 0);
  return { from: start.toISOString(), to: end.toISOString() };
}

/** Devise par défaut du backend (`Constants.DEFAULT_CURRENCY`). */
export const DEFAULT_CURRENCY = 'EUR';

/** Montant total : `total` au contrat, `amount` + `currency` à plat au backend (déduit). */
export function costTotal(stats: ContractCostStats | undefined): { amount: number; currency: string } {
  const amount =
    typeof stats?.total === 'number' ? stats.total : typeof stats?.amount === 'number' ? stats.amount : 0;
  return { amount, currency: stats?.currency || DEFAULT_CURRENCY };
}

export function formatMoney(amount: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 2 }).format(
      amount,
    );
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export type CostBucket = { start: string; value: number };

/** Au-delà de ce nombre de périodes, les barres passent de la journée à la semaine. */
export const MAX_DAILY_BARS = 62;

/** Lundi (UTC) de la semaine d'une date ISO. */
function weekStart(iso: string): string {
  const date = new Date(iso);
  date.setUTCHours(0, 0, 0, 0);
  const shift = (date.getUTCDay() + 6) % 7;
  return new Date(date.getTime() - shift * DAY).toISOString();
}

/**
 * Coût par période (`period[i]` ↔ `budget[i]` : le backend range le coût de chaque période dans
 * `budget`, nom hérité du legacy). Trié par date ; regroupé par semaine au-delà de
 * `MAX_DAILY_BARS` périodes pour que chaque barre reste lisible.
 */
export function costBuckets(stats: ContractCostStats | undefined): {
  unit: 'day' | 'week';
  buckets: CostBucket[];
} {
  const periods = stats?.period ?? [];
  const values = stats?.budget ?? [];
  const daily = periods
    .map((start, index) => ({ start, value: values[index] ?? 0 }))
    .filter((bucket) => !Number.isNaN(Date.parse(bucket.start)))
    .sort((a, b) => a.start.localeCompare(b.start));
  if (daily.length <= MAX_DAILY_BARS) return { unit: 'day', buckets: daily };
  const weeks = new Map<string, number>();
  for (const bucket of daily)
    weeks.set(weekStart(bucket.start), (weeks.get(weekStart(bucket.start)) ?? 0) + bucket.value);
  return {
    unit: 'week',
    buckets: [...weeks.entries()].map(([start, value]) => ({ start, value: Math.round(value * 100) / 100 })),
  };
}

/** Unité d'une quantité d'usage selon son type (`CostManager` : instance à la minute, egress et journaux au Go). */
export const usageUnit = (type: UsageProductType | undefined): 'minutes' | 'gigabytes' | 'units' =>
  type === 'INSTANCE' ? 'minutes' : type === 'EGRESS' || type === 'LOGS' ? 'gigabytes' : 'units';

export type UsageLine = {
  key: string;
  type: UsageProductType | 'UNKNOWN';
  reference: string;
  quantity: number;
  unit: ReturnType<typeof usageUnit>;
  /** Jobs distincts qui ont consommé (dimension `job_id`), pour une instance. */
  jobs: number;
  last: string | undefined;
};

/** Regroupe les lignes d'usage par type de produit et référence (type d'instance, `egress`, `logs`). */
export function aggregateUsages(usages: readonly ContractUsage[]): UsageLine[] {
  const lines = new Map<string, UsageLine & { jobIds: Set<string> }>();
  for (const usage of usages) {
    const type = usage.product_type ?? 'UNKNOWN';
    const reference = usage.product_reference ?? '—';
    const key = `${type}:${reference}`;
    const line = lines.get(key) ?? {
      key,
      type,
      reference,
      quantity: 0,
      unit: usageUnit(usage.product_type),
      jobs: 0,
      last: undefined,
      jobIds: new Set<string>(),
    };
    line.quantity = Math.round((line.quantity + (usage.quantity ?? 0)) * 100) / 100;
    const job = usage.dimensions?.['job_id'];
    if (job) line.jobIds.add(job);
    if (usage.created && (!line.last || usage.created > line.last)) line.last = usage.created;
    lines.set(key, line);
  }
  return [...lines.values()].map(({ jobIds, ...line }) => ({ ...line, jobs: jobIds.size }));
}

/** Ventilation par type de produit (`by_type`), dans un ordre stable et sans les types à zéro. */
export function costByType(stats: ContractCostStats | undefined): { type: string; amount: number }[] {
  const order = ['INSTANCE', 'EGRESS', 'LOGS', 'SUPPORT'];
  return Object.entries(stats?.by_type ?? {})
    .filter(([, amount]) => typeof amount === 'number' && amount > 0)
    .map(([type, amount]) => ({ type, amount }))
    .sort((a, b) => {
      const ia = order.indexOf(a.type);
      const ib = order.indexOf(b.type);
      return (ia < 0 ? order.length : ia) - (ib < 0 ? order.length : ib) || a.type.localeCompare(b.type);
    });
}

/** Une ligne du tableau « Coûts par projet » (`GET /costs/projects`, F8.12). */
export type ProjectCostRow = {
  /** Clé stable : l'identifiant du projet, `''` pour l'usage rattaché à aucun projet. */
  key: string;
  projectId: string | undefined;
  /** Nom résolu par `GET /projects` quand il est lisible ; sinon `undefined` (l'écran montre l'id). */
  name: string | undefined;
  amount: number;
  currency: string;
  byType: { type: string; amount: number }[];
  unpriced: string[];
  /** Taux d'utilisation (ADR 0089) ; `undefined` sans usage d'instance sur la période. */
  utilization: Utilization | undefined;
};

/**
 * Le taux d'utilisation d'un projet tel que l'écran le montre (ADR 0089, chantier 4). `cpu` et `memory` sont des
 * rapports (0–1, au-delà de 1 possible) ou `null` quand rien n'a été mesuré — jamais 0 pour « inconnu ».
 */
export type Utilization = {
  cpu: number | null;
  memory: number | null;
  cpuAllocated: number;
  cpuUsed: number | null;
  memoryAllocated: number | null;
  memoryUsed: number | null;
  /** Part des vCPU × heures alloués qui a une mesure (0–1), `null` si inconnue. */
  coverage: number | null;
};

/** En deçà, la réservation est surdimensionnée : alerte douce (skill `dataviz` : jamais la couleur seule). */
export const LOW_UTILIZATION = 0.3;

export type UtilizationLevel = 'low' | 'normal' | 'unknown';

export function utilizationLevel(ratio: number | null | undefined): UtilizationLevel {
  if (ratio === null || ratio === undefined || Number.isNaN(ratio)) return 'unknown';
  return ratio < LOW_UTILIZATION ? 'low' : 'normal';
}

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Le taux d'une ligne du contrat ; `undefined` si elle ne porte aucun alloué (pas d'usage d'instance). */
export function utilizationOf(cost: ContractProjectCost): Utilization | undefined {
  const cpuAllocated = finite(cost.cpu_allocated_core_hours);
  if (cpuAllocated === null) return undefined;
  return {
    cpu: finite(cost.cpu_utilization),
    memory: finite(cost.memory_utilization),
    cpuAllocated,
    cpuUsed: finite(cost.cpu_used_core_hours),
    memoryAllocated: finite(cost.memory_allocated_gb_hours),
    memoryUsed: finite(cost.memory_used_gb_hours),
    coverage: finite(cost.utilization_coverage),
  };
}

/** Un rapport en pourcentage entier (`0.3333` → `33 %`), dans la langue de l'écran. */
export function formatRatio(ratio: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(ratio);
}

/** Des heures (vCPU × h, Gio × h) à une décimale au plus. */
export function formatHours(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: value < 10 ? 2 : 1 }).format(value);
}

/**
 * Lignes du tableau des coûts par projet, dans l'ordre du serveur (du plus cher au moins cher). Le
 * nom d'un projet vient de la liste des projets QUAND elle est lisible : un projet qu'on ne peut pas
 * lire (ou supprimé depuis) garde son identifiant brut, l'usage sans projet n'a ni id ni nom.
 */
export function projectCostRows(
  costs: readonly ContractProjectCost[] | undefined,
  projects: readonly { id?: string; name?: string }[] | undefined,
): ProjectCostRow[] {
  const names = new Map<string, string>();
  for (const project of projects ?? []) if (project.id && project.name) names.set(project.id, project.name);
  return (costs ?? []).map((cost) => {
    const projectId = cost.project_id?.trim() ? cost.project_id : undefined;
    return {
      key: projectId ?? '',
      projectId,
      name: projectId ? names.get(projectId) : undefined,
      amount: typeof cost.amount === 'number' ? cost.amount : 0,
      currency: cost.currency || DEFAULT_CURRENCY,
      byType: costByType({ by_type: cost.by_type ?? {} }),
      unpriced: [...new Set(cost.unpriced ?? [])].sort(),
      utilization: utilizationOf(cost),
    };
  });
}
